import React, { useEffect, useState } from 'react';
import {
  Container, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Box, TextField, MenuItem, Select, FormControl, InputLabel, Button, Alert, Chip
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';

const karatOptions = ['22K', '24K', '22/22K', '24/24K', '18K', 'PT95', 'WG'];

const ShopPendingList = () => {
  const [rows, setRows] = useState([]);
  const [karigarFilter, setKarigarFilter] = useState('');
  const [karatFilter, setKaratFilter] = useState('');
  const [karigars, setKarigars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [unissuing, setUnissuing] = useState(null);
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pendingRes, karigarsRes] = await Promise.all([
        api.get('/api/shop/pending-list'),
        api.get('/api/karigar')
      ]);
      setRows(pendingRes.data);
      setKarigars(karigarsRes.data);
    } catch (err) {
      setError('Failed to fetch pending list data');
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleUnissue = async (row) => {
    const key = `${row.orderId}-${row.itemId}`;
    setUnissuing(key);
    setError('');
    try {
      await api.post('/api/shop/unissue', {
        orderId: row.orderId,
        itemId: row.itemId,
        isDirect: row.isDirect
      });
      await fetchData();
    } catch (err) {
      setError('Failed to unissue item');
    }
    setUnissuing(null);
  };

  const filteredRows = rows.filter(row => {
    const karigarMatch = !karigarFilter || row.karigar === karigarFilter;
    const karatMatch = !karatFilter || row.karat === karatFilter;
    return karigarMatch && karatMatch;
  });

  const formatDate = (d) => {
    if (!d) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
    return new Date(d).toLocaleDateString('en-GB');
  };

  return (
    <Container maxWidth="xl" sx={{ py: 6 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} variant="outlined" color="primary" sx={{ mb: 2 }}>
          Back
        </Button>
        <Typography variant="h4" gutterBottom color="primary">Shop Pending List</Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 180 }} size="small">
            <InputLabel>Karigar</InputLabel>
            <Select value={karigarFilter} label="Karigar" onChange={e => setKarigarFilter(e.target.value)}>
              <MenuItem value="">All Karigars</MenuItem>
              {karigars.map(k => (
                <MenuItem key={k.abbreviation} value={k.abbreviation}>{k.abbreviation} - {k.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 120 }} size="small">
            <InputLabel>Karat</InputLabel>
            <Select value={karatFilter} label="Karat" onChange={e => setKaratFilter(e.target.value)}>
              <MenuItem value="">All Karats</MenuItem>
              {karatOptions.map(k => <MenuItem key={k} value={k}>{k}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
            Total: {filteredRows.length} items
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress /></Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Bill</TableCell>
                  <TableCell>Item Name</TableCell>
                  <TableCell>Karigar</TableCell>
                  <TableCell>Karat</TableCell>
                  <TableCell>Weight</TableCell>
                  <TableCell>Qty</TableCell>
                  <TableCell>Required Date</TableCell>
                  <TableCell>Issue Date</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">No pending items found</TableCell>
                  </TableRow>
                ) : filteredRows.map((row, index) => {
                  const key = `${row.orderId}-${row.itemId}`;
                  return (
                    <TableRow key={`${key}-${index}`}>
                      <TableCell>{row.billName} {row.billNumber}</TableCell>
                      <TableCell>{row.itemName}</TableCell>
                      <TableCell>{row.karigar}</TableCell>
                      <TableCell>
                        <Chip label={row.karat} size="small" color="primary" variant="outlined" />
                      </TableCell>
                      <TableCell>{row.itemWeight}</TableCell>
                      <TableCell>{row.quantity}</TableCell>
                      <TableCell>{formatDate(row.requiredDate)}</TableCell>
                      <TableCell>{formatDate(row.issueDate)}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          disabled={unissuing === key}
                          onClick={() => handleUnissue(row)}
                        >
                          {unissuing === key ? 'Unissuing...' : 'Unissue'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Container>
  );
};

export default ShopPendingList;
